'use client';

import * as React from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter
} from './AlertDialog';
import { Input } from '../Input';
import { Button } from '../buttons';

const AlertDialogContext = React.createContext<
  (
    params: AlertAction
  ) => Promise<
    AlertAction['type'] extends 'alert' | 'confirm' ? boolean : null | string
  >
>(() => null!);

type ButtonVariant = 'brand' | 'cta' | 'danger' | 'basic';

const defaultCancelButtonText = 'Cancel';
const defaultActionButtonText = 'Confirm';

type AlertAction =
  | {
      type: 'alert';
      title: string;
      body?: string;
      cancelButton?: string;
      cancelButtonVariant?: ButtonVariant;
    }
  | {
      type: 'confirm';
      title: string;
      body?: string;
      cancelButton?: string;
      actionButton?: string;
      cancelButtonVariant?: ButtonVariant;
      actionButtonVariant?: ButtonVariant;
    }
  | {
      type: 'prompt';
      title: string;
      body?: string;
      cancelButton?: string;
      actionButton?: string;
      defaultValue?: string;
      cancelButtonVariant?: ButtonVariant;
      actionButtonVariant?: ButtonVariant;
      inputProps?: React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
      >;
    }
  | { type: 'close' };

interface AlertDialogState {
  open: boolean;
  title: string;
  body: string;
  type: 'alert' | 'confirm' | 'prompt';
  cancelButton: string;
  actionButton: string;
  cancelButtonVariant: ButtonVariant;
  actionButtonVariant: ButtonVariant;
  defaultValue?: string;
  inputProps?: React.PropsWithoutRef<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >
  >;
}

function alertDialogReducer(
  state: AlertDialogState,
  action: AlertAction
): AlertDialogState {
  switch (action.type) {
    case 'close':
      return { ...state, open: false };
    case 'alert':
    case 'confirm':
    case 'prompt':
      return {
        ...state,
        open: true,
        ...action,
        cancelButton:
          action.cancelButton ||
          (action.type === 'alert'
            ? defaultActionButtonText
            : defaultCancelButtonText),
        actionButton:
          ('actionButton' in action && action.actionButton) ||
          defaultActionButtonText,
        cancelButtonVariant: action.cancelButtonVariant || 'basic',
        actionButtonVariant:
          ('actionButtonVariant' in action && action.actionButtonVariant) ||
          'brand'
      };
    default:
      return state;
  }
}

function AlertProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(alertDialogReducer, {
    open: false,
    title: '',
    body: '',
    type: 'alert',
    cancelButton: defaultCancelButtonText,
    actionButton: defaultActionButtonText,
    cancelButtonVariant: 'basic',
    actionButtonVariant: 'brand'
  });

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resolveRef = React.useRef<((tf: any) => void) | undefined>(undefined);

  function close() {
    dispatch({ type: 'close' });

    resolveRef.current?.(false);
  }

  function confirm(value?: string) {
    dispatch({ type: 'close' });

    resolveRef.current?.(value ?? true);
  }

  const dialog = React.useCallback(async <T extends AlertAction>(params: T) => {
    dispatch(params);

    return new Promise<
      T['type'] extends 'alert' | 'confirm' ? boolean : null | string
    >((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  return (
    <AlertDialogContext.Provider value={dialog}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(open) => {
          if (!open) close();
          return;
        }}
      >
        <AlertDialogContent asChild>
          <form
            onSubmit={(event) => {
              event.preventDefault();

              confirm(event.currentTarget.prompt?.value);
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{state.title}</AlertDialogTitle>
              {state.body ? (
                <AlertDialogDescription>{state.body}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            {state.type === 'prompt' && (
              //@ts-expect-error - inputProps is not a valid prop for Input
              <Input
                name={'prompt'}
                defaultValue={state.defaultValue}
                {...state.inputProps}
                size={'large'}
              />
            )}
            <AlertDialogFooter>
              <Button
                type={'button'}
                onClick={close}
                buttonType={state.cancelButtonVariant}
              >
                {state.cancelButton}
              </Button>
              {state.type === 'alert' ? null : (
                <Button type={'submit'} buttonType={state.actionButtonVariant}>
                  {state.actionButton}
                </Button>
              )}
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </AlertDialogContext.Provider>
  );
}

type Params<T extends 'alert' | 'confirm' | 'prompt'> =
  | Omit<Extract<AlertAction, { type: T }>, 'type'>
  | string;

function useConfirm() {
  const dialog = React.useContext(AlertDialogContext);

  return React.useCallback(
    (params: Params<'confirm'>) =>
      dialog({
        ...(typeof params === 'string' ? { title: params } : params),
        type: 'confirm'
      }),
    [dialog]
  );
}

function usePrompt() {
  const dialog = React.useContext(AlertDialogContext);

  return (params: Params<'prompt'>) =>
    dialog({
      ...(typeof params === 'string' ? { title: params } : params),
      type: 'prompt'
    });
}

function useAlert() {
  const dialog = React.useContext(AlertDialogContext);

  return (params: Params<'alert'>) =>
    dialog({
      ...(typeof params === 'string' ? { title: params } : params),
      type: 'alert'
    });
}

export type { AlertAction, AlertDialogState };
export { AlertProvider, useAlert, useConfirm, usePrompt, AlertDialogContext };
